import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import { Theme } from '@mui/material';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import * as React from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectAsrcPlayer, selectIsPlaying } from '../../selectors/message';

// HITL 3.0 volume control hosted inside the header Player's hover popover.
// Uncontrolled (the thumb tracks the cursor natively) and driven by a parent-
// owned `valueRef` so the level survives the popover closing/reopening. The OLD
// inline transcript volume control is the separate, unchanged `Audio/VolumeSlider`.
const useStyles = makeStyles<Theme>(({}) => ({
  boxClass: {
    width: '100%',
    height: '40px',
  },
}));

const MIN_DB = -30;
const MAX_DB = 30;

interface HeaderVolumeSliderProps {
  // Persisted level holder kept by the Player (which stays mounted), so the
  // volume survives the popover closing/reopening.
  valueRef: React.MutableRefObject<number>;
  // Current mute state + change callback, so the header icon can reflect "0 sound".
  isMuted: boolean;
  onMutedChange: (muted: boolean) => void;
}

export const HeaderVolumeSlider = ({
  valueRef,
  isMuted,
  onMutedChange,
}: HeaderVolumeSliderProps) => {
  const { boxClass } = useStyles();
  const isPlaying = useAppSelector(selectIsPlaying);
  const asrcPlayer = useAppSelector(selectAsrcPlayer);

  const dbRef = valueRef;
  // Remount the uncontrolled slider when the icons jump it to min/max.
  const [sliderKey, setSliderKey] = React.useState(0);

  // Set the gain value directly — cheapest, applies immediately, and never
  // throws (the scheduling APIs threw on every tick here, stalling the drag).
  const setGain = (gain: number) => {
    const param = asrcPlayer.gainNode?.gain;
    if (!param) return;
    try {
      param.value = gain;
    } catch {
      // keep the UI responsive even if gain can't be applied
    }
  };

  // Uncontrolled: update gain + persisted ref on drag WITHOUT re-rendering, so
  // the thumb tracks the cursor natively (smooth/immediate).
  const handleChange = (_event: Event, newValue: number | Array<number>) => {
    const db = newValue as number;
    dbRef.current = db;
    setGain(10 ** (db / 20));
    // Only fire on the mute -> unmute transition (not every drag tick).
    if (isMuted) onMutedChange(false);
  };

  const handleMute = () => {
    dbRef.current = MIN_DB;
    setGain(0);
    onMutedChange(true);
    setSliderKey((k) => k + 1);
  };
  const handleFull = () => {
    dbRef.current = MAX_DB;
    setGain(10 ** (MAX_DB / 20));
    onMutedChange(false);
    setSliderKey((k) => k + 1);
  };

  if (!isPlaying) return null;

  return (
    <Box className={boxClass}>
      <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
        <VolumeDown
          sx={{ cursor: 'pointer' }}
          onClick={handleMute}
          aria-label="Mute"
        />
        <Slider
          key={sliderKey}
          aria-label="Volume"
          min={MIN_DB} // decibel
          max={MAX_DB} // decibel
          defaultValue={dbRef.current}
          onChange={handleChange}
        />
        <VolumeUp
          sx={{ cursor: 'pointer' }}
          onClick={handleFull}
          aria-label="Full volume"
        />
      </Stack>
    </Box>
  );
};
